/**
 * HeaderCard - Sticky site header with logo, navigation, and CTA.
 */
import React, { useState, useEffect } from 'react';
import { assetUrl } from '../../lib/assets';

export interface NavItem {
  label: string;
  href: string;
  cta?: boolean;
}

export interface HeaderCardProps {
  variant?: string;
  siteName?: string;
  logoSrc?: string;
  logo?: string;
  logo_alt?: string;
  logoPosition?: 'left' | 'right';
  nav?: NavItem[];
  nav_items?: NavItem[];
  cta_label?: string;
  cta_href?: string;
  className?: string;
}

const HeaderCard: React.FC<HeaderCardProps> = (props) => {
  const {
    siteName = 'Thornless Data Management',
    logoPosition = 'left',
    nav,
    nav_items,
    cta_label,
    cta_href,
    className = '',
  } = props;
  const logoSrc = props.logoSrc || props.logo;
  const navItems = nav || nav_items || [];
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`nav-card ${scrolled ? 'scrolled' : ''} ${className}`}
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 'var(--z-sticky)',
        backgroundColor: 'var(--color-primary)',
        boxShadow: scrolled ? '0 2px 8px rgba(0,0,0,0.25)' : 'none',
        transition: 'box-shadow var(--transition-base)',
      }}
      role="banner"
    >
      <div className="layout-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '4rem', flexDirection: logoPosition === 'right' ? 'row-reverse' : 'row' }}>
        {/* Logo */}
        <a href="/" aria-label={`${siteName} home`} style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', textDecoration: 'none' }}>
          {logoSrc && (
            <img src={assetUrl(logoSrc)} alt={siteName} style={{ height: '32px', width: 'auto' }} />
          )}
          <span className="nav-site-name" style={{ fontFamily: 'var(--font-family-heading)', fontWeight: 'var(--font-weight-bold)', color: '#ffffff' }}>
            {siteName}
          </span>
        </a>

        {/* Desktop Nav (hidden on mobile via .nav-desktop media query) */}
        <nav aria-label="Main navigation" className="nav-desktop">
          {navItems.map((item) =>
            item.cta ? (
              <a key={item.label} href={item.href} className="btn btn-sm" style={{ backgroundColor: '#ffffff', color: 'var(--color-primary)', borderColor: '#ffffff' }}>
                {item.label}
              </a>
            ) : (
              <a
                key={item.label}
                href={item.href}
                style={{
                  color: 'rgba(255,255,255,0.85)',
                  textDecoration: 'none',
                  fontWeight: 'var(--font-weight-medium)',
                  fontSize: 'var(--font-size-sm)',
                  transition: 'color var(--transition-fast)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#ffffff')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.85)')}
              >
                {item.label}
              </a>
            )
          )}
          {cta_label && (
            <a href={cta_href || '/contact'} className="btn btn-sm" style={{ backgroundColor: '#ffffff', color: 'var(--color-primary)', borderColor: '#ffffff' }}>{cta_label}</a>
          )}
        </nav>

        {/* Mobile menu toggle (hidden on desktop via .mobile-menu-btn media query) */}
        <button
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen(!mobileOpen)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 'var(--spacing-2)', color: '#ffffff' }}
          className="mobile-menu-btn"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" aria-hidden="true">
            {mobileOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div
          style={{
            borderTop: '1px solid rgba(255,255,255,0.2)',
            backgroundColor: 'var(--color-primary)',
            padding: 'var(--spacing-4)',
          }}
        >
          <nav aria-label="Mobile navigation" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className={item.cta ? 'btn btn-primary' : ''}
                style={item.cta ? { backgroundColor: '#ffffff', color: 'var(--color-primary)', borderColor: '#ffffff' } : { color: 'rgba(255,255,255,0.85)', textDecoration: 'none', padding: 'var(--spacing-2) 0', fontWeight: 'var(--font-weight-medium)' }}
                onClick={() => setMobileOpen(false)}
              >
                {item.label}
              </a>
            ))}
            {cta_label && (
              <a href={cta_href || '/contact'} className="btn" style={{ backgroundColor: '#ffffff', color: 'var(--color-primary)', borderColor: '#ffffff' }} onClick={() => setMobileOpen(false)}>
                {cta_label}
              </a>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default HeaderCard;
